import React from "react";
import { Button } from "antd";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ACTIONS, usePermissions } from "hooks/permissions/permissions";
import "./style.css";

/**
 * Route guard: renders `children` only when the user holds `action` on `page`.
 *
 * - `form` decides the action from the `:id` param, the same way every form
 *   screen decides create versus edit: `create` needs ADD, anything else EDIT.
 * - `redirect` sends a denied user straight to the first page they can view
 *   (used for the dashboard, which is where login lands everyone) instead of
 *   showing the no-access message.
 *
 * While permissions are loading it shows a spinner rather than the page, so a
 * form cannot be filled in before we know it is allowed.
 */
const RequirePermission = ({ page, action = ACTIONS.VIEW, form = false, redirect = false, children }) => {
    const { id } = useParams();
    const { pathname } = useLocation();
    const { loaded, isError, refetch, can, firstPermittedPath } = usePermissions();

    const required = form ? (id === "create" ? ACTIONS.ADD : ACTIONS.EDIT) : action;

    if (!loaded) {
        return (
            <div className="access-state">
                {isError ? (
                    <>
                        <h2>We couldn&apos;t load your permissions</h2>
                        <p>Check your connection and try again.</p>
                        <Button type="primary" onClick={() => refetch()}>Retry</Button>
                    </>
                ) : (
                    <span className="table-state-spinner" role="status" aria-label="Loading" />
                )}
            </div>
        );
    }

    if (can(page, required)) {
        return children;
    }

    if (redirect && firstPermittedPath && firstPermittedPath !== pathname) {
        return <Navigate to={firstPermittedPath} replace />;
    }

    return (
        <div className="access-state">
            <h2>You don&apos;t have access to this page</h2>
            <p>Your role does not allow this. Ask an administrator if you need it.</p>
            {firstPermittedPath && firstPermittedPath !== pathname ? (
                <Link to={firstPermittedPath}>
                    <Button type="primary">Go to a page you can access</Button>
                </Link>
            ) : null}
        </div>
    );
};

export default RequirePermission;
